import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  BarChart3, Activity as ActivityIcon, Calendar, Phone, MessageSquare,
  TrendingUp, Loader2, Trophy, Users, Reply, Target, AlertTriangle, Clock, Lightbulb,
  Zap, AlertCircle, Pause, XCircle, RefreshCw,
} from "lucide-react";

const STAGE_LABEL = {
  scraped: "Scraped", contacted: "Contacted", qualified: "Qualified",
  demo: "Demo", won: "Won", lost: "Lost",
};
const STAGE_COLOR = {
  scraped: "#71717A", contacted: "#3B82F6", qualified: "#A855F7",
  demo: "#F59E0B", won: "#10B981", lost: "#EF4444",
};
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const pct = (n) => `${Math.round((n || 0) * 100)}%`;

function StatCard({ label, value, sub, icon: Icon, accent = "#002FA7", testid }) {
  return (
    <div className="border border-zinc-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">{label}</div>
        <Icon className="w-4 h-4 text-zinc-400" />
      </div>
      <div className="font-heading text-3xl font-black tracking-tight" style={{ color: accent }}>
        {value}
      </div>
      {sub && <div className="text-xs text-zinc-500 mt-1.5 font-mono">{sub}</div>}
    </div>
  );
}

function FunnelRow({ stage, count, max }) {
  const w = max > 0 ? Math.max(2, Math.round((count / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-3 py-1.5" data-testid={`funnel-row-${stage}`}>
      <div className="w-24 text-xs font-bold uppercase tracking-wider text-zinc-600">{STAGE_LABEL[stage]}</div>
      <div className="flex-1 bg-zinc-100 h-7 relative">
        <div
          className="h-full transition-all"
          style={{ width: `${w}%`, background: STAGE_COLOR[stage] }}
        />
        <span className="absolute inset-0 flex items-center px-2 text-xs font-mono font-bold text-white mix-blend-difference">
          {count}
        </span>
      </div>
    </div>
  );
}

function Heatmap({ heatmap }) {
  // heatmap shape: {0: {0: count, ...}, ...}
  const max = useMemo(() => {
    let m = 0;
    if (!heatmap) return 0;
    for (const d of Object.keys(heatmap)) {
      for (const h of Object.keys(heatmap[d] || {})) {
        if (heatmap[d][h] > m) m = heatmap[d][h];
      }
    }
    return m;
  }, [heatmap]);

  const cell = (d, h) => {
    const v = heatmap?.[d]?.[h] ?? 0;
    const opacity = max > 0 ? Math.max(0.06, v / max) : 0.06;
    return (
      <div
        key={`${d}-${h}`}
        className="w-7 h-7 border border-white text-[8px] font-mono flex items-center justify-center"
        style={{ background: v > 0 ? `rgba(0, 47, 167, ${opacity})` : "#F4F4F5", color: opacity > 0.5 ? "#fff" : "#52525B" }}
        title={`${WEEKDAYS[d]} ${h}:00 — ${v} replies`}
        data-testid={`heatmap-cell-${d}-${h}`}
      >
        {v > 0 ? v : ""}
      </div>
    );
  };

  return (
    <div className="overflow-x-auto" data-testid="heatmap">
      <div className="inline-flex flex-col gap-px min-w-fit">
        <div className="flex gap-px ml-12">
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="w-7 text-[9px] font-mono text-center text-zinc-400">
              {h % 3 === 0 ? h : ""}
            </div>
          ))}
        </div>
        {Array.from({ length: 7 }, (_, d) => (
          <div key={d} className="flex gap-px items-center">
            <div className="w-12 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
              {WEEKDAYS[d]}
            </div>
            {Array.from({ length: 24 }, (_, h) => cell(d, h))}
          </div>
        ))}
      </div>
      <div className="text-[9px] text-zinc-400 font-mono mt-2 ml-12">
        ← hour (UTC, 0–23) · darker = more inbound replies
      </div>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [campaignId, setCampaignId] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [queue, setQueue] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: cs } = await api.get("/campaigns");
        setCampaigns(cs);
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Poll queue stats every 15s — cheap counts, indexed queries.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/queue-stats");
        if (!cancelled) setQueue(data);
      } catch (e) { /* silent — non-critical */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { days };
      if (campaignId) params.campaign_id = campaignId;
      const { data } = await api.get("/analytics/overview", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load analytics");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, campaignId]);

  const totals = data?.totals;
  const funnelGlobal = data?.funnel_global || [];
  const maxFunnel = Math.max(1, ...funnelGlobal.map((r) => r.count));
  const perTouch = data?.per_touch || [];
  const channelTotals = data?.channel_totals || {};
  const recommendations = data?.recommendations || [];
  const byCampaign = data?.funnel_by_campaign || [];

  return (
    <div className="p-8 max-w-[1400px]" data-testid="analytics-page">
      <div className="border-b border-zinc-200 pb-6 mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline mb-2">/ analytics</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight">Pipeline Analytics</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Funnel, per-touch response rate, best-hour heatmap, and campaign A/B comparison.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="select-days"
            className="px-3 py-2 text-xs border border-zinc-300 bg-white font-mono focus:outline-none focus:border-[#002FA7]"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last year</option>
          </select>
          <select
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            data-testid="select-campaign-filter"
            className="px-3 py-2 text-xs border border-zinc-300 bg-white focus:outline-none focus:border-[#002FA7]"
          >
            <option value="">All campaigns</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-24 text-zinc-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading analytics…
        </div>
      ) : (
        <>
          {/* Top-row KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="analytics-kpis">
            <StatCard
              label="Leads"
              value={totals?.leads ?? 0}
              sub="in selected window"
              icon={Users}
              testid="kpi-leads"
            />
            <StatCard
              label="Contacted"
              value={totals?.leads_contacted ?? 0}
              sub={`${pct((totals?.leads_contacted || 0) / (totals?.leads || 1))} of leads`}
              icon={MessageSquare}
              accent="#3B82F6"
              testid="kpi-contacted"
            />
            <StatCard
              label="Responded"
              value={totals?.leads_responded ?? 0}
              sub={`${pct(totals?.response_rate)} reply rate`}
              icon={Reply}
              accent="#A855F7"
              testid="kpi-responded"
            />
            <StatCard
              label="Qualified+"
              value={totals?.qualified ?? 0}
              sub="qualified · demo · won"
              icon={Target}
              accent="#F59E0B"
              testid="kpi-qualified"
            />
            <StatCard
              label="Won"
              value={totals?.won ?? 0}
              sub={`${pct(totals?.conversion_rate)} conversion`}
              icon={Trophy}
              accent="#10B981"
              testid="kpi-won"
            />
          </div>

          {/* Queue Health — live snapshot of the sequence runner */}
          <QueueHealthCard queue={queue} />

          {/* Funnel + Per-touch */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="border border-zinc-200 bg-white p-6" data-testid="funnel-global">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline mb-1">/ funnel · global</div>
                  <h2 className="font-heading text-xl font-black">Pipeline by Stage</h2>
                </div>
                <BarChart3 className="w-5 h-5 text-zinc-400" />
              </div>
              <div className="space-y-1">
                {funnelGlobal.map((r) => (
                  <FunnelRow key={r.stage} stage={r.stage} count={r.count} max={maxFunnel} />
                ))}
              </div>
            </div>

            <div className="border border-zinc-200 bg-white p-6" data-testid="per-touch">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline mb-1">/ per-touch response rate</div>
                  <h2 className="font-heading text-xl font-black">Cadence Performance</h2>
                </div>
                <TrendingUp className="w-5 h-5 text-zinc-400" />
              </div>
              {perTouch.length === 0 ? (
                <div className="text-sm text-zinc-500 py-8 text-center">
                  No outbound touches yet in this window — run a sequence to populate this chart.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200">
                      <th className="text-left py-2 overline">Step</th>
                      <th className="text-left py-2 overline">Channel</th>
                      <th className="text-right py-2 overline">Sent</th>
                      <th className="text-right py-2 overline">Replied</th>
                      <th className="text-right py-2 overline">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perTouch.map((t) => (
                      <tr key={t.step} className="border-b border-zinc-100" data-testid={`per-touch-row-${t.step}`}>
                        <td className="py-2 font-mono font-bold">
                          {t.manual ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800">manual</span>
                            </span>
                          ) : (
                            `#${t.step}`
                          )}
                        </td>
                        <td className="py-2">
                          <span className="inline-flex items-center gap-1 text-xs uppercase font-bold tracking-wider">
                            {t.kind === "call" ? <Phone className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                            {t.kind}
                          </span>
                        </td>
                        <td className="py-2 text-right font-mono">{t.sent}</td>
                        <td className="py-2 text-right font-mono">{t.responded}</td>
                        <td className="py-2 text-right font-mono font-bold" style={{
                          color: t.rate >= 0.15 ? "#10B981" : t.rate >= 0.05 ? "#F59E0B" : "#71717A",
                        }}>
                          {pct(t.rate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="text-[10px] text-zinc-400 font-mono mt-3">
                A reply is any inbound SMS or call within 7 days of an outbound step. Step #0 = manual/bulk-blast actions.
              </p>
            </div>
          </div>

          {/* Channel totals — every outbound, not just cadence */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-testid="channel-totals">
            <ChannelCard
              label="SMS"
              channel="sms"
              icon={MessageSquare}
              accent="#3B82F6"
              data={channelTotals.sms || { sent: 0, responded: 0, rate: 0, unique_leads: 0 }}
            />
            <ChannelCard
              label="Calls"
              channel="call"
              icon={Phone}
              accent="#A855F7"
              data={channelTotals.call || { sent: 0, responded: 0, rate: 0, unique_leads: 0 }}
            />
          </div>

          {/* Best Touch Winner — A/B-test-like recommendations */}
          <RecommendationsCard recommendations={recommendations} campaigns={campaigns} onApplied={load} />

          {/* Heatmap */}
          <div className="border border-zinc-200 bg-white p-6 mb-8" data-testid="heatmap-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline mb-1">/ best hour to reach prospects</div>
                <h2 className="font-heading text-xl font-black">Reply Heatmap</h2>
              </div>
              <Calendar className="w-5 h-5 text-zinc-400" />
            </div>
            <Heatmap heatmap={data?.heatmap} />
            {data?.best_slots?.length > 0 && (
              <div className="mt-5 border-t border-zinc-100 pt-4">
                <div className="overline mb-2">Top 5 reply windows</div>
                <div className="flex flex-wrap gap-2">
                  {data.best_slots.map((s, i) => (
                    <div
                      key={i}
                      className="px-3 py-1.5 bg-blue-50 text-[#002FA7] text-xs font-mono font-bold"
                      data-testid={`best-slot-${i}`}
                    >
                      {WEEKDAYS[s.weekday]} {String(s.hour).padStart(2, "0")}:00 · {s.count} replies
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Campaign A/B comparison */}
          <div className="border border-zinc-200 bg-white p-6" data-testid="campaign-compare">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline mb-1">/ campaign A/B</div>
                <h2 className="font-heading text-xl font-black">By Campaign</h2>
              </div>
              <ActivityIcon className="w-5 h-5 text-zinc-400" />
            </div>
            {byCampaign.length === 0 ? (
              <div className="text-sm text-zinc-500 py-8 text-center">
                No campaign data in this window.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-2 overline">Campaign</th>
                    <th className="text-right py-2 overline">Total</th>
                    {["scraped", "contacted", "answered", "qualified", "booked", "demo", "won", "lost"].map((s) => (
                      <th key={s} className="text-right py-2 overline" style={{ color: STAGE_COLOR[s] }}>
                        {STAGE_LABEL[s]}
                      </th>
                    ))}
                    <th className="text-right py-2 overline">Win %</th>
                  </tr>
                </thead>
                <tbody>
                  {byCampaign.map((c) => {
                    const winRate = c.total > 0 ? c.funnel.won / c.total : 0;
                    return (
                      <tr key={c.campaign_id || "none"} className="border-b border-zinc-100" data-testid={`campaign-row-${c.campaign_id || "none"}`}>
                        <td className="py-2 font-semibold">{c.name}</td>
                        <td className="py-2 text-right font-mono font-bold">{c.total}</td>
                        {["scraped", "contacted", "answered", "qualified", "booked", "demo", "won", "lost"].map((s) => (
                          <td key={s} className="py-2 text-right font-mono">{c.funnel[s]}</td>
                        ))}
                        <td className="py-2 text-right font-mono font-bold" style={{
                          color: winRate >= 0.05 ? "#10B981" : winRate > 0 ? "#F59E0B" : "#71717A",
                        }}>
                          {pct(winRate)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}


function ChannelCard({ label, channel, icon: Icon, accent, data }) {
  const rate = data.rate || 0;
  return (
    <div className="border border-zinc-200 bg-white p-5" data-testid={`channel-${channel}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: accent }} />
          <span className="overline">/ {channel} totals (all outbound)</span>
        </div>
        <span
          className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5"
          style={{ background: rate >= 0.15 ? "#D1FAE5" : rate >= 0.05 ? "#FEF3C7" : "#F4F4F5", color: rate >= 0.15 ? "#065F46" : rate >= 0.05 ? "#92400E" : "#52525B" }}
        >
          {Math.round(rate * 100)}% reply
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="font-mono text-2xl font-bold" style={{ color: accent }}>{data.sent}</div>
          <div className="overline mt-1">sent</div>
        </div>
        <div>
          <div className="font-mono text-2xl font-bold">{data.responded}</div>
          <div className="overline mt-1">replied</div>
        </div>
        <div>
          <div className="font-mono text-2xl font-bold">{data.unique_leads || 0}</div>
          <div className="overline mt-1">unique leads</div>
        </div>
      </div>
    </div>
  );
}


function RecommendationsCard({ recommendations, campaigns = [], onApplied }) {
  const [openFor, setOpenFor] = useState(null); // key of the reco that has picker open
  const [picking, setPicking] = useState({}); // {key: campaignId}
  const [applying, setApplying] = useState({});

  const SEV = {
    win:  { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", chip: "bg-emerald-600", icon: Trophy },
    warn: { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-900",   chip: "bg-amber-600",   icon: AlertTriangle },
    info: { bg: "bg-blue-50",    border: "border-blue-200",    text: "text-blue-900",    chip: "bg-blue-600",    icon: Clock },
  };

  const handleApply = async (key, reco) => {
    const cid = picking[key];
    if (!cid) {
      toast.error("Pick a campaign first");
      return;
    }
    setApplying((m) => ({ ...m, [key]: true }));
    try {
      const { data } = await api.post("/analytics/apply-best-touch", {
        campaign_id: cid,
        step: reco.step,
        kind: reco.kind,
      });
      toast.success(data.message || "Applied to campaign", { duration: 5000 });
      setOpenFor(null);
      if (typeof onApplied === "function") await onApplied();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to apply step");
    } finally {
      setApplying((m) => ({ ...m, [key]: false }));
    }
  };

  return (
    <div className="border border-zinc-200 bg-white p-5 mb-8" data-testid="recommendations-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline">/ best touch winner</div>
          <h2 className="font-heading text-xl font-bold mt-1 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" /> AI Cadence Recommendations
          </h2>
          <p className="text-xs text-zinc-500 mt-1 font-mono">
            Auto-detected from your last 60 days. Hit "Apply to Campaign" on a win to clone that step into your cadence.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {recommendations.map((r, idx) => {
          const key = `${r.type}-${idx}`;
          const sev = SEV[r.severity] || SEV.info;
          const Icon = sev.icon;
          const canApply = r.severity === "win" && r.kind && typeof r.step === "number";
          const isOpen = openFor === key;
          return (
            <div
              key={key}
              className={`p-4 border ${sev.border} ${sev.bg} ${sev.text}`}
              data-testid={`reco-${r.type}`}
            >
              <div className="flex items-start gap-2.5">
                <span className={`w-7 h-7 ${sev.chip} text-white flex items-center justify-center shrink-0`}>
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold uppercase tracking-wider mb-1">{r.label}</div>
                  <p className="text-sm leading-relaxed">{r.message}</p>

                  {canApply && (
                    <div className="mt-3 pt-3 border-t border-emerald-200/60">
                      {!isOpen ? (
                        <button
                          type="button"
                          onClick={() => setOpenFor(key)}
                          data-testid={`reco-apply-open-${r.type}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold uppercase tracking-widest transition"
                        >
                          <Target className="w-3 h-3" /> Apply to Campaign
                        </button>
                      ) : (
                        <div className="space-y-2" data-testid={`reco-apply-picker-${r.type}`}>
                          <label className="block text-[10px] font-bold uppercase tracking-wider text-emerald-900">
                            Append this winning touch to:
                          </label>
                          <select
                            value={picking[key] || ""}
                            onChange={(e) => setPicking((m) => ({ ...m, [key]: e.target.value }))}
                            data-testid={`reco-apply-select-${r.type}`}
                            className="w-full px-2.5 py-2 border border-emerald-300 bg-white text-zinc-900 text-xs focus:outline-none focus:border-emerald-600"
                          >
                            <option value="">— pick a campaign —</option>
                            {campaigns.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleApply(key, r)}
                              disabled={applying[key] || !picking[key]}
                              data-testid={`reco-apply-confirm-${r.type}`}
                              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-[11px] font-bold uppercase tracking-widest inline-flex items-center gap-1.5 transition"
                            >
                              {applying[key] ? <><Loader2 className="w-3 h-3 animate-spin" /> Applying…</> : <>Confirm</>}
                            </button>
                            <button
                              type="button"
                              onClick={() => setOpenFor(null)}
                              data-testid={`reco-apply-cancel-${r.type}`}
                              className="px-3 py-1.5 border border-emerald-300 hover:bg-emerald-100 text-[11px] font-bold uppercase tracking-widest transition"
                            >
                              Cancel
                            </button>
                          </div>
                          {campaigns.length === 0 && (
                            <p className="text-[10px] text-emerald-900/70 mt-1">
                              No campaigns yet — create one in <strong>/campaign</strong> first.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/**
 * Queue Health — live snapshot of the sequence runner. Polls every 15s. Designed
 * to fit one row above the funnel so operators can spot trouble at a glance:
 *   - high `stuck_claims` → a worker crashed mid-step
 *   - growing `dead_letter` → bad phone numbers / Twilio config
 *   - `due_now > batch_size * 10` → workers can't keep up with the schedule
 */
function QueueHealthCard({ queue }) {
  if (!queue) {
    return (
      <div className="border border-zinc-200 bg-white p-5 mb-8" data-testid="queue-health-loading">
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading queue health…
        </div>
      </div>
    );
  }
  const q = queue.queue || {};
  const t = queue.throughput || {};
  const cfg = queue.config || {};
  const hasIssue = (q.stuck_claims || 0) > 0 || (q.dead_letter || 0) > 0;
  return (
    <div className="border border-zinc-200 bg-white p-5 mb-8" data-testid="queue-health-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <Zap className="w-3 h-3 text-[#002FA7]" /> queue health · live
          </div>
          <h2 className="font-heading text-xl font-black">Sequence Runner</h2>
        </div>
        <div className="text-[10px] font-mono text-zinc-500 text-right">
          concurrency {cfg.concurrency} · batch {cfg.batch_size}<br />
          retries {cfg.max_retries} · TCPA {cfg.tcpa_quiet_start}-{cfg.tcpa_quiet_end}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <QStat label="Due now" value={q.due_now} icon={Clock} testid="q-due" accent="#002FA7" />
        <QStat label="In flight" value={q.in_flight} icon={Zap} testid="q-inflight" accent="#3B82F6" />
        <QStat label="Scheduled" value={q.scheduled_future} icon={Calendar} testid="q-scheduled" accent="#737373" />
        <QStat label="Retrying" value={q.retrying} icon={RefreshCw} testid="q-retrying" accent="#F59E0B" warn={q.retrying > 5} />
        <QStat label="Dead-letter" value={q.dead_letter} icon={XCircle} testid="q-dead" accent="#DC2626" warn={q.dead_letter > 0} />
        <QStat label="Paused" value={q.paused} icon={Pause} testid="q-paused" accent="#737373" />
        <QStat label="Stuck" value={q.stuck_claims} icon={AlertCircle} testid="q-stuck" accent="#DC2626" warn={q.stuck_claims > 0} />
      </div>
      <div className="mt-4 pt-4 border-t border-zinc-100 flex items-center justify-between text-xs text-zinc-600 flex-wrap gap-2">
        <div className="flex items-center gap-4">
          <span>
            <span className="font-mono font-bold text-zinc-900">{t.outbound_last_hour ?? 0}</span> outbound · last hour
          </span>
          <span>
            <span className="font-mono font-bold text-zinc-900">{t.outbound_last_day ?? 0}</span> · last 24h
          </span>
        </div>
        {hasIssue && (
          <div className="flex items-center gap-1.5 text-red-700 text-[11px] font-bold uppercase tracking-wider" data-testid="queue-health-warning">
            <AlertTriangle className="w-3.5 h-3.5" /> Needs attention
          </div>
        )}
      </div>
    </div>
  );
}

function QStat({ label, value, icon: Icon, accent, testid, warn = false }) {
  return (
    <div className={`border ${warn ? "border-red-200 bg-red-50" : "border-zinc-100 bg-zinc-50"} p-3`} data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="overline text-[9px]">{label}</span>
        <Icon className="w-3 h-3" style={{ color: accent }} />
      </div>
      <div className="font-heading text-2xl font-black" style={warn ? { color: "#DC2626" } : null}>
        {value ?? 0}
      </div>
    </div>
  );
}
